import { cloneElement, isValidElement, type CSSProperties, type ReactNode } from 'react';
import type { PortableTextComponents } from '@portabletext/react';

import { richTextComponent } from '@/sanity/sanityPortableText';

import hatch from '@/public/patrick-affiliate/hero-hatch.png';
import hatchLight from '@/public/patrick-affiliate/hatch-light.png';

/**
 * The "measured" highlight every heading on this page uses: a translucent
 * wash with a fine hatch texture, bracketed by two tick marks — a rule along
 * the left edge with a square hanging below it, and a rule along the right
 * edge with a square standing on top. Figma draws it as loose rectangles
 * behind the text; here it hangs off the existing highlight decorators so
 * editors keep the marks they already know.
 *
 * Geometry is *not* proportional to the type size in the design. Every 64px
 * and 72px heading gets the same 74px wash, 6px squares and 1.5px rules; the
 * mobile frames use 47px / 3.6px / 1.5px. So the sizes live in rem (px / 8 at
 * the design width) with a breakpoint switch, and the headings the designer
 * drew differently override them through CSS custom properties set on the
 * heading wrapper:
 *
 *   --hl-wash      wash height                       (47px mobile / 74px desktop)
 *   --hl-ext       left rule overshoot past the wash (3px mobile / 0 desktop)
 *   --hl-dy        wash offset from the line-box centre, +down (1px)
 *   --hl-pl/--hl-pr wash padding past the text box   (3px / 0.15em)
 *   --hl-tracking  letter-spacing of the highlighted words (heading's by default;
 *                  half the headings use -0.03em where the heading is -0.05em)
 *   --hl-ml/--hl-mr extra room before / after the highlight — the design types
 *                  a double space next to the ticks where a word follows
 *   --hl-ctr       optical-centring correction; `0px` opts a heading out
 *   --hl-deco      `none` hides the wash and ticks (testimonials mobile)
 *
 * Optical centring: the marks are absolutely positioned, so `text-align: center`
 * centres the words alone, and since the right-hand marks reach further than the
 * left (the wash clears the italic overhang), the decorated run leans right.
 * --hl-ctr moves half that difference from the left margin to the right. This is
 * a deliberate ~3px departure from the frames, which centre the text box and let
 * the marks fall where they land — design review asked for it twice.
 *
 * Vertical placement: the design centres the wash on the *line box*. Blacker
 * Display's ascender/descender split differs from Geist's, so the serif span's
 * own box sits 0.086em lower than the line — the wash is lifted by that much.
 * The page also zeroes the line-height of every span inside a heading (see
 * PagePatrickAffiliateJSX) so a Blacker span cannot stretch the line: it would
 * otherwise add 5–8px under every line that carries a highlight.
 *
 * The wrapper forces Blacker Italic itself: editors stack the serif and
 * highlight marks in either order, and `typography.scss` gives every `span`
 * the Geist family, so nothing about the font may be left to inheritance.
 */

/** (ascender − |descender|) / 2: Geist 0.355em, Instrument Sans 0.36em, Blacker Italic 0.269em. */
const LINE_CENTRE_LIFT = '0.0875em';

const WRAPPER_CLASS = [
  // `inline` (not inline-block) so the box is the font's content area; nowrap
  // because the absolutely positioned wash cannot follow a line break.
  'relative inline whitespace-nowrap !font-blacker italic font-normal leading-[0]',
  // Breakpoint defaults; a section overrides the un-suffixed variable.
  '[--hl-wash-d:5.875rem] lg:[--hl-wash-d:9.25rem]',
  '[--hl-ext-d:0.375rem] lg:[--hl-ext-d:0rem]',
  '[--hl-tracking-d:-0.05em]',
  // Wash padding: a hair on the left, enough on the right to clear the italic overhang.
  '[--hl-pl-d:0.375rem] [--hl-pr-d:0.15em]',
  // Right square 3.6px / 6px, left square 3.066px / 5.116px, hatch tile 6px / 10px.
  '[--hl-sr:0.45rem] lg:[--hl-sr:0.75rem]',
  '[--hl-sl:0.3833rem] lg:[--hl-sl:0.6395rem]',
  '[--hl-tex:0.75rem] lg:[--hl-tex:1.25rem]',
  // The `em` decorator pads italics on the right; inside the wash that reads as a gap.
  '[&_em]:pr-0',
  // typography.scss gives every `span` the Geist family; that rule would beat
  // inheritance on the text span below and on any nested decorator span.
  '[&_span]:!font-blacker',
].join(' ');

const WRAPPER_STYLE = {
  '--hl-w': 'var(--hl-wash, var(--hl-wash-d))',
  '--hl-x': 'var(--hl-ext, var(--hl-ext-d))',
  '--hl-p-l': 'var(--hl-pl, var(--hl-pl-d))',
  '--hl-p-r': 'var(--hl-pr, var(--hl-pr-d))',
  '--hl-r': 'max(1px, 0.1875rem)',
  // Wash top: line-box centre, nudged by --hl-dy (the design sits ~1px below
  // it on most headings), minus half the wash.
  '--hl-top': `calc(50% - ${LINE_CENTRE_LIFT} + var(--hl-dy, 0.125rem) - var(--hl-w) / 2)`,
  // How far the marks reach past the text box on each side (see the optical
  // centring note above).
  '--hl-reach-l': 'calc(var(--hl-p-l) + 0.25rem + var(--hl-sl))',
  '--hl-reach-r': 'calc(var(--hl-p-r) + 0.25rem + var(--hl-sr))',
  // `-d` and read back through `var(--hl-ctr, …)` like every other knob: this
  // object lands *on* the wrapper, so a bare `--hl-ctr` here would outrank the
  // section's class and could never be overridden.
  '--hl-ctr-d': 'calc((var(--hl-reach-r) - var(--hl-reach-l)) / 2)',
  '--hl-c': 'var(--hl-ctr, var(--hl-ctr-d))',
  letterSpacing: 'var(--hl-tracking, var(--hl-tracking-d))',
  marginLeft: 'calc(var(--hl-ml, 0px) - var(--hl-c))',
  marginRight: 'calc(var(--hl-mr, 0px) + var(--hl-c))',
} as CSSProperties;

const DECO_STYLE = {
  display: 'var(--hl-deco, block)',
} as CSSProperties;

const WASH_BOX: CSSProperties = {
  ...DECO_STYLE,
  top: 'var(--hl-top)',
  left: 'calc(-1 * var(--hl-p-l))',
  right: 'calc(-1 * var(--hl-p-r))',
  height: 'var(--hl-w)',
};

type Tone = 'light' | 'dark' | 'forest';

const TONES: Record<
  Tone,
  { wash: string; tick: string; text: string; texture: string }
> = {
  light: {
    wash: 'rgba(34,142,113,0.12)',
    tick: '#228E71',
    text: '#003828',
    texture: hatch.src,
  },
  dark: {
    wash: 'rgba(218,255,245,0.12)',
    tick: '#BAE9D6',
    text: '#D6EEE7',
    texture: hatchLight.src,
  },
  forest: {
    wash: 'rgba(11,91,69,0.1)',
    tick: '#0B5B45',
    text: '#003828',
    texture: hatch.src,
  },
};

/** Ordinary whitespace only — `\s` would also eat the zero-width characters stega appends in draft mode. */
const LEAD = /^[ \t\r\n]+/;
const TRAIL = /[ \t\r\n]+$/;

/**
 * Splits the whitespace an editor left at either end of the highlighted text
 * out of the wash: "Your VIP Advantage| in 3 Steps|" must start the wash at
 * "in", and the space has to stay outside so the line can break on it.
 * Walks into arrays and single-child elements (a nested serif / em mark) so
 * the mark order the editor happened to use does not matter.
 */
function trimEdges(children: ReactNode): [string, ReactNode, string] {
  if (typeof children === 'string') {
    const before = children.match(LEAD)?.[0] ?? '';
    const after = children.match(TRAIL)?.[0] ?? '';
    const core = children.slice(before.length, children.length - after.length);
    return core ? [before, core, after] : ['', children, ''];
  }
  if (Array.isArray(children)) {
    if (children.length === 1) return trimEdges(children[0]);
    const items = [...children];
    let before = '';
    let after = '';
    if (typeof items[0] === 'string') {
      const [b, core] = trimEdges(items[0]);
      before = b;
      items[0] = core;
    }
    const last = items.length - 1;
    if (typeof items[last] === 'string') {
      const [, core, a] = trimEdges(items[last]);
      after = a;
      items[last] = core;
    }
    return [before, items, after];
  }
  if (isValidElement<{ children?: ReactNode }>(children)) {
    const [before, core, after] = trimEdges(children.props.children);
    if (!before && !after) return ['', children, ''];
    return [before, cloneElement(children, undefined, core), after];
  }
  return ['', children, ''];
}

export function MeasuredHighlight({
  children,
  tone = 'light',
}: {
  children: ReactNode;
  tone?: Tone;
}) {
  const { wash, tick, text, texture } = TONES[tone];
  const [before, core, after] = trimEdges(children);

  return (
    <>
      {before}
      <span className={WRAPPER_CLASS} style={{ ...WRAPPER_STYLE, color: text }}>
        <span
          aria-hidden
          className="absolute pointer-events-none hl-wash"
          style={{ ...WASH_BOX, backgroundColor: wash }}
        />
        <span
          aria-hidden
          className="absolute pointer-events-none opacity-[0.09] hl-texture"
          style={{
            ...WASH_BOX,
            backgroundImage: `url(${texture})`,
            backgroundSize: 'var(--hl-tex) var(--hl-tex)',
            backgroundPosition: 'top left',
          }}
        />
        {/* Left tick: rule from the wash top, overshooting by --hl-ext, square below. */}
        <span
          aria-hidden
          className="absolute pointer-events-none hl-tick-l"
          style={{
            ...DECO_STYLE,
            top: 'var(--hl-top)',
            right: 'calc(100% + var(--hl-p-l) + 0.25rem)',
            width: 'var(--hl-sl)',
          }}
        >
          <span
            className="block mx-auto"
            style={{
              width: 'var(--hl-r)',
              height: 'calc(var(--hl-w) + var(--hl-x))',
              backgroundColor: tick,
            }}
          />
          <span className="block w-full aspect-square" style={{ backgroundColor: tick }} />
        </span>
        {/* Right tick: square on top, rule starting 1px above the wash and ending with it. */}
        <span
          aria-hidden
          className="absolute pointer-events-none hl-tick-r"
          style={{
            ...DECO_STYLE,
            top: 'calc(var(--hl-top) - var(--hl-sr) - 0.125rem)',
            left: 'calc(100% + var(--hl-p-r) + 0.25rem)',
            width: 'var(--hl-sr)',
          }}
        >
          <span className="block w-full aspect-square" style={{ backgroundColor: tick }} />
          <span
            className="block mx-auto"
            style={{
              width: 'var(--hl-r)',
              height: 'calc(var(--hl-w) + 0.125rem)',
              backgroundColor: tick,
            }}
          />
        </span>
        <span className="relative !font-blacker">{core}</span>
      </span>
      {after}
    </>
  );
}

/**
 * The serif decorators in this design's colours: #003828 on light sections,
 * #D6EEE7 on dark ones (the shared marks use #DDF2E9).
 */
const serifMarks: PortableTextComponents['marks'] = {
  serif: ({ children }) => <span className="!font-blacker">{children}</span>,
  darkGreenSerif: ({ children }) => (
    <span className="!font-blacker font-normal italic text-[#003828]">{children}</span>
  ),
  lightGreenSerif: ({ children }) => (
    <span className="!font-blacker font-normal italic text-[#D6EEE7]">{children}</span>
  ),
};

/** Light sections: mint wash, #228E71 ticks. Overrides `lightGreenHighlight`. */
export const measuredRichText: PortableTextComponents = {
  ...richTextComponent,
  marks: {
    ...richTextComponent.marks,
    ...serifMarks,
    lightGreenHighlight: ({ children }) => (
      <MeasuredHighlight tone="light">{children}</MeasuredHighlight>
    ),
  },
};

/** Dark sections: pale wash, #BAE9D6 ticks, #D6EEE7 text. Overrides `darkGreenHighlight`. */
export const measuredRichTextDark: PortableTextComponents = {
  ...richTextComponent,
  marks: {
    ...richTextComponent.marks,
    ...serifMarks,
    darkGreenHighlight: ({ children }) => (
      <MeasuredHighlight tone="dark">{children}</MeasuredHighlight>
    ),
  },
};

export const measuredRichTextForest: PortableTextComponents = {
  ...richTextComponent,
  marks: {
    ...richTextComponent.marks,
    ...serifMarks,
    lightGreenHighlight: ({ children }) => (
      <MeasuredHighlight tone="forest">{children}</MeasuredHighlight>
    ),
  },
};
